import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import { EncapsulatedPDFStorage } from 'domains/viewer/Viewer/SOPClasses';
import { ViewType } from 'generated/graphql';
import { mat3, vec3 } from 'gl-matrix';
import type {
  Frame,
  FullSingleLayerStack,
  Stack,
  ViewportDisplayConfiguration,
} from '../../../ViewportsConfigurations/types';
import type { BaseImagingProvider } from './BaseImagingProvider';

//////////////////////////////////////////////////////////////////////////////////////////
//
// Taken from: https://github.com/KitwareMedical/VolView/blob/master/src/utils/lps.ts#L79
//
//////////////////////////////////////////////////////////////////////////////////////////

type DirectionIndex = 0 | 1 | 2;

// A small tolerance (in mm) used when projecting slice-to-slice displacement
// onto the image normal. 0.5 mm filters out sub-millimeter rounding or scanner
// jitter in ImagePositionPatient coordinates without hiding real motion.
const SLICE_DIRECTION_TOLERANCE_MM = 0.1;

/**
 * Maps LPS direction to world-space direction (not index-space direction)
 */
export type LPSDirections = {
  // These should match columns of the current image orientation matrix.
  Left: vec3;
  Right: vec3;
  Posterior: vec3;
  Anterior: vec3;
  Superior: vec3;
  Inferior: vec3;
  // maps LPS axis to column in direction matrix
  Coronal: DirectionIndex;
  Sagittal: DirectionIndex;
  Axial: DirectionIndex;
};

export enum InferiorToSuperiorCheck {
  InferiorToSuperior = 'InferiorToSuperior',
  SuperiorToInferior = 'SuperiorToInferior',
  Unknown = 'Unknown',
}

export const defaultLPSDirections: () => LPSDirections = () => ({
  Left: vec3.fromValues(1, 0, 0),
  Right: vec3.fromValues(-1, 0, 0),
  Posterior: vec3.fromValues(0, 1, 0),
  Anterior: vec3.fromValues(0, -1, 0),
  Superior: vec3.fromValues(0, 0, 1),
  Inferior: vec3.fromValues(0, 0, -1),

  Sagittal: 0,
  Coronal: 1,
  Axial: 2,
});

/**
 * Associates the column vectors of a 3x3 matrix with the LPS axes.
 *
 * For each of the LPS axes, this function returns the associated column index (0, 1, 2)
 * in the provided 3x3 column-major matrix.
 *
 * Approach:
 *   - find the max of the direction matrix, ignoring columns and rows marked as done
 *   - assign the column vector of that max value to the row axis
 *   - mark that row and column as done
 *   - continue until all rows and columns are done
 */
export function getLPSDirections(direction: mat3): LPSDirections {
  // Track the rows and columns that have yet to be assigned.
  const availableCols: [DirectionIndex, DirectionIndex, DirectionIndex] = [0, 1, 2];
  const availableRows: [DirectionIndex, DirectionIndex, DirectionIndex] = [0, 1, 2];
  const lpsDirs: LPSDirections = defaultLPSDirections();
  for (let i = 0; i < 3; i++) {
    let bestValue = 0;
    let bestValueLoc: [DirectionIndex, DirectionIndex] = [0, 0]; // col, row
    let removeIndices = [0, 0]; // indices into availableCols/Rows for deletion

    availableCols.forEach((col, colIdx) => {
      availableRows.forEach((row, rowIdx) => {
        const value = direction[col * 3 + row];
        if (Math.abs(value) > Math.abs(bestValue)) {
          bestValue = value;
          bestValueLoc = [col, row];
          removeIndices = [colIdx, rowIdx];
        }
      });
    });

    // the row index corresponds to the index of the LPS axis
    const [col, axis] = bestValueLoc;
    const axisVector = direction.slice(col * 3, (col + 1) * 3);
    const vecSign = Math.sign(bestValue);
    const posVector = axisVector.map((c: number) => c * vecSign);
    const negVector = axisVector.map((c: number) => c * -vecSign);
    if (axis === 0) {
      // Sagittal
      lpsDirs.Sagittal = col;
      lpsDirs.Left = [posVector[0], posVector[1], posVector[2]];
      lpsDirs.Right = [negVector[0], negVector[1], negVector[2]];
    } else if (axis === 1) {
      // Coronal
      lpsDirs.Coronal = col;
      lpsDirs.Posterior = [posVector[0], posVector[1], posVector[2]];
      lpsDirs.Anterior = [negVector[0], negVector[1], negVector[2]];
    } else if (axis === 2) {
      // Axial
      lpsDirs.Axial = col;
      lpsDirs.Superior = [posVector[0], posVector[1], posVector[2]];
      lpsDirs.Inferior = [negVector[0], negVector[1], negVector[2]];
    }

    availableCols.splice(removeIndices[0], 1);
    availableRows.splice(removeIndices[1], 1);
  }

  return lpsDirs;
}

export const getDefaultViewtypeForStack = (stack: Stack): ViewType => {
  const isEncapsulatedPdf =
    'frames' in stack && stack?.frames[0].sopClassUID === EncapsulatedPDFStorage;
  return isEncapsulatedPdf ? ViewType.Pdf : ViewType.TwoDDre;
};

export function createVtkImageDataInstanceForFrame(frame: Frame): vtkImageData {
  const extent =
    frame.size.length >= 2
      ? [0, frame.size[0] - 1, 0, frame.size[1] - 1, 0, 0]
      : [0, 1, 0, 1, 0, 0];

  return vtkImageData.newInstance({
    spacing: frame.spacing,
    origin: frame.origin,
    direction: frame.direction.data,
    extent,
  });
}

type PerFrameAnnotationImage = {
  buffer: ArrayBufferLike;
  byteOffset: number;
  length: number;
  colorChannels?: number;
  image: vtkImageData;
};

// One per-frame ROI image shared across annotations/viewports so N ellipses don't each rebuild it.
// Keyed on pixel buffer/offset/length since getFramePixels returns a fresh view each call.
const perFrameAnnotationImageCache = new WeakMap<object, Map<string, PerFrameAnnotationImage>>();

export const getSharedPerFrameAnnotationImage = (
  provider: BaseImagingProvider<Stack>,
  frameSmid: string,
  frameTags: Frame,
  colorChannels?: number
): Nullable<vtkImageData> => {
  const framePixels = provider.getFramePixels(frameSmid);
  if (framePixels == null) return undefined;

  let byFrame = perFrameAnnotationImageCache.get(provider);
  if (byFrame == null) {
    byFrame = new Map();
    perFrameAnnotationImageCache.set(provider, byFrame);
  }
  const cached = byFrame.get(frameSmid);
  if (
    cached != null &&
    cached.buffer === framePixels.buffer &&
    cached.byteOffset === framePixels.byteOffset &&
    cached.length === framePixels.length &&
    cached.colorChannels === colorChannels
  ) {
    return cached.image;
  }

  const image = createVtkImageDataInstanceForFrame(frameTags);
  const scalars = vtkDataArray.newInstance({
    name: 'Scalars',
    numberOfComponents: colorChannels,
    values: framePixels,
  });
  image.getPointData().setScalars(scalars);
  byFrame.set(frameSmid, {
    buffer: framePixels.buffer,
    byteOffset: framePixels.byteOffset,
    length: framePixels.length,
    colorChannels,
    image,
  });
  return image;
};

/** Release a provider's cached per-frame ROI images and free their VTK objects (call on dispose). */
export const disposeSharedPerFrameAnnotationImages = (provider: object): void => {
  const byFrame = perFrameAnnotationImageCache.get(provider);
  if (byFrame == null) return;
  for (const { image } of byFrame.values()) {
    (image as { delete?: () => void }).delete?.();
  }
  byFrame.clear();
  perFrameAnnotationImageCache.delete(provider);
};

export const instanceCountMatch = (
  imageDataSetA: vtkImageData,
  imageDataSetB: vtkImageData
): boolean => {
  return getInstanceCount(imageDataSetA) === getInstanceCount(imageDataSetB);
};

export const getInstanceCount = (vtkImage: vtkImageData): number | null => {
  if (vtkImage == null) return null;
  const extent = vtkImage.getExtent();
  if (extent == null) return null;
  const zMin = extent[4];
  const zMax = extent[5];
  return zMax - zMin + 1;
};

export function getKRange(image: vtkImageData) {
  const [, , , , kMin, kMax] = image.getExtent();
  return { kMin, kMax };
}

export function getSliceDirection(
  imagePositionPatientList: number[][],
  imageOrientationPatient: number[]
): InferiorToSuperiorCheck {
  if (
    !imageOrientationPatient ||
    imageOrientationPatient.length !== 6 ||
    !imagePositionPatientList ||
    imagePositionPatientList.length < 2
  ) {
    return InferiorToSuperiorCheck.Unknown;
  }

  // Extract row and column direction cosines from the orientation vector
  const rowDirectionCosine = [
    imageOrientationPatient[0],
    imageOrientationPatient[1],
    imageOrientationPatient[2],
  ];
  const columnDirectionCosine = [
    imageOrientationPatient[3],
    imageOrientationPatient[4],
    imageOrientationPatient[5],
  ];

  // Compute the slice normal vector using the cross product of row and column direction cosines
  const normalVector = [
    rowDirectionCosine[1] * columnDirectionCosine[2] -
      rowDirectionCosine[2] * columnDirectionCosine[1],
    rowDirectionCosine[2] * columnDirectionCosine[0] -
      rowDirectionCosine[0] * columnDirectionCosine[2],
    rowDirectionCosine[0] * columnDirectionCosine[1] -
      rowDirectionCosine[1] * columnDirectionCosine[0],
  ];

  let positiveDirectionCount = 0;
  let negativeDirectionCount = 0;

  // Iterate through consecutive image positions to determine slice ordering
  for (let i = 0; i < imagePositionPatientList.length - 1; i++) {
    const positionDelta = [
      imagePositionPatientList[i + 1][0] - imagePositionPatientList[i][0],
      imagePositionPatientList[i + 1][1] - imagePositionPatientList[i][1],
      imagePositionPatientList[i + 1][2] - imagePositionPatientList[i][2],
    ];

    // Project the delta vector onto the normal to determine relative slice movement
    const projectionOntoNormal =
      positionDelta[0] * normalVector[0] +
      positionDelta[1] * normalVector[1] +
      positionDelta[2] * normalVector[2];

    if (Math.abs(projectionOntoNormal) <= SLICE_DIRECTION_TOLERANCE_MM) continue;

    if (projectionOntoNormal > 0) positiveDirectionCount++;
    else negativeDirectionCount++;
  }

  const totalComparisons = positiveDirectionCount + negativeDirectionCount;
  if (!totalComparisons) return InferiorToSuperiorCheck.Unknown;

  const normalZComponent = normalVector[2];
  if (Math.abs(normalZComponent) < 0.2) return InferiorToSuperiorCheck.Unknown;

  // Determine direction based on slice order and normal orientation
  if (positiveDirectionCount > negativeDirectionCount)
    return normalZComponent > 0
      ? InferiorToSuperiorCheck.InferiorToSuperior
      : InferiorToSuperiorCheck.SuperiorToInferior;
  else
    return normalZComponent > 0
      ? InferiorToSuperiorCheck.SuperiorToInferior
      : InferiorToSuperiorCheck.InferiorToSuperior;
}

export function getInitialSlice(
  image: Nullable<vtkImageData>,
  invertImageOrder: Nullable<boolean>,
  startingImageSetting: string | null,
  viewportDisplayConfig?: Nullable<ViewportDisplayConfiguration>
): number {
  const defaultImageNumber = viewportDisplayConfig?.params2d?.imageNumber ?? 0;
  if (!image) return defaultImageNumber;

  const { kMax } = getKRange(image);
  if (kMax === null || kMax === undefined) return defaultImageNumber;

  switch (startingImageSetting) {
    case 'middle': {
      const middleIndex = Math.max(
        invertImageOrder ? Math.ceil(kMax / 2) : Math.floor(kMax / 2),
        0
      );
      return middleIndex;
    }
    case 'last':
      return invertImageOrder ? 0 : kMax;
    default:
      return invertImageOrder ? kMax : 0;
  }
}

export function isStackInferiorToSuperior(stack: FullSingleLayerStack): InferiorToSuperiorCheck {
  const frames = stack?.frames ?? [];
  if (frames.length < 2) return InferiorToSuperiorCheck.Unknown;

  const firstFrame = frames[0];
  const lastFrame = frames[frames.length - 1];

  const ipp0 = firstFrame.imagePositionPatient;
  const ippN = lastFrame.imagePositionPatient;
  const iop = firstFrame.imageOrientationPatient; // orientation should be identical for the whole stack

  if (!Array.isArray(ipp0) || !Array.isArray(ippN) || !Array.isArray(iop)) {
    return InferiorToSuperiorCheck.Unknown;
  }

  const imagePositionList = [Array.from(ipp0), Array.from(ippN)];
  const imageOrientation = Array.from(iop);

  return getSliceDirection(imagePositionList, imageOrientation);
}
export const calculateDefaultWindowLevel = (
  range?: [number, number] | null
): {
  window: number;
  level: number;
} => {
  const fallback = { window: 1, level: 0 } as const;
  if (range == null) return fallback;
  const [minLevelValue, maxLevelValue]: [number, number] = range;
  const middle = (minLevelValue + maxLevelValue) / 2;
  const window = maxLevelValue - minLevelValue;
  return {
    window,
    level: middle,
  };
};
